import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles, formatCount } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { usePageSeo } from "@/hooks/usePageSeo";

const modes = [
  { key: "heat", label: "热度总榜" },
  { key: "views", label: "围观最多" },
  { key: "talk", label: "讨论最多" },
];

export default function Hot() {
  const [mode, setMode] = useState("heat");

  usePageSeo({
    title: "实时热点榜 - 吃瓜现场 | 今日热搜八卦排行",
    description:
      "吃瓜现场实时热点榜：按热度、围观量、讨论量为你整理今日娱乐八卦、综艺现场、社会热点与网络热梗排行，免下载直接打开实时浏览。",
  });

  const list = useMemo(() => {
    const sorted = [...articles];
    if (mode === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else if (mode === "views") sorted.sort((a, b) => b.views - a.views);
    else sorted.sort((a, b) => b.comments - a.comments);
    return sorted;
  }, [mode]);

  const top3 = list.slice(0, 3);
  const rest = list.slice(3);

  const rankClass = (i: number) =>
    i === 0
      ? "text-primary-500"
      : i === 1
      ? "text-secondary-500"
      : i === 2
      ? "text-accent-600"
      : "text-foreground-300";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
      {/* 面包屑 */}
      <nav className="flex items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
        <span className="text-foreground-950">实时热点榜</span>
      </nav>

      {/* 标题区 */}
      <div className="mt-5 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-500">实时更新</p>
          <h1 className="mt-2 flex items-center gap-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50 md:h-11 md:w-11">
              <i className="ri-fire-fill text-xl" aria-hidden="true"></i>
            </span>
            实时热点榜
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700">
            吃瓜网页免下载在线看，手机版电脑版同步实时刷新。这里汇聚当前最受关注的娱乐八卦、综艺现场、社会热点与网络热梗，跟上热搜节奏不再错过。
          </p>
        </div>
      </div>

      {/* 排序切换 */}
      <div className="mt-6 flex flex-wrap items-center gap-2 rounded-full bg-background-100 p-1 md:w-fit">
        {modes.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMode(m.key)}
            className={`flex-1 whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors cursor-pointer md:flex-none ${
              mode === m.key ? "bg-foreground-950 text-background-50" : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* 前三名突出 */}
      <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" aria-label="热点前三">
        {top3.map((a, i) => (
          <Link
            key={a.slug}
            to={`/article/${a.slug}`}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50"
          >
            <img src={a.cover} alt={`${a.title}封面`} title={`${a.title} - 吃瓜现场`} className="aspect-[16/9] w-full object-cover" />
            <span
              className={`absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl font-heading text-xl font-black text-background-50 ${
                i === 0 ? "bg-primary-500" : i === 1 ? "bg-secondary-500" : "bg-accent-600"
              }`}
            >
              {i + 1}
            </span>
            <div className="flex flex-1 flex-col p-4">
              <span className="text-xs text-foreground-600">{a.catName} · {a.date}</span>
              <h2 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                {a.title}
              </h2>
              <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-600">
                <span className="inline-flex items-center gap-1 text-primary-600">
                  <i className="ri-fire-fill" aria-hidden="true"></i> {a.heat}° 热
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true"></i> {formatCount(a.views)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-chat-3-line" aria-hidden="true"></i> {formatCount(a.comments)}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </section>

      {/* 榜单主体 + 侧栏 */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2" aria-label="热点榜单">
          <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
            <i className="ri-list-check-3 text-primary-500" aria-hidden="true"></i>
            完整榜单
          </h2>
          <div className="mt-3 divide-y divide-background-200/70 rounded-2xl border border-background-200/70 bg-background-50">
            {rest.map((a, idx) => {
              const rank = idx + 4;
              return (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-background-100/60"
                >
                  <span className={`w-8 shrink-0 text-center font-heading text-2xl font-black ${rankClass(idx)}`}>
                    {rank}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="line-clamp-1 text-sm font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">
                      {a.title}
                    </span>
                    <span className="mt-1 flex items-center gap-3 text-xs text-foreground-600">
                      <span>{a.catName}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-secondary-500" aria-hidden="true"></i> {a.heat}°
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line" aria-hidden="true"></i> {formatCount(a.views)}
                      </span>
                    </span>
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </Link>
              );
            })}
          </div>
        </section>

        <aside className="space-y-6" aria-label="侧栏">
          {/* 按频道浏览 */}
          <div className="rounded-2xl border border-background-200/70 bg-background-100/50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-black text-foreground-950">
              <i className="ri-apps-2-line text-primary-500" aria-hidden="true"></i> 按频道浏览
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-3.5 py-2 text-sm text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {c.name}
                  <span className="text-xs text-foreground-500">{c.count}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* 立即阅读提醒 */}
          <div className="rounded-2xl bg-accent-900 p-5 text-accent-50">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-700/70 text-background-50">
              <i className="ri-eye-off-line text-lg" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-background-50">换个姿势继续吃瓜</h3>
            <p className="mt-2 text-sm leading-relaxed text-accent-100/90">
              热门内容持续刷新中。网页在线看、直接打开、免下载，无需注册即可继续浏览全部热点详情。
            </p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-background-50 px-4 py-2 text-sm font-medium text-accent-900 transition-colors hover:bg-accent-100"
            >
              回到首页
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}